import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { VariantBarcodesUpdateRequest } from '@/api/handlers/resources/products/requests';
import type { GetProductDetailResponse } from '@/api/handlers/resources/products/responses';
import type { ApiResponse, ErrorResponse } from '@/api/handlers/responses';
import type { ProductVariant } from '@/api/models';
import { ProductQueryKeys } from './productQueryKeys';

export type UpdateVariantBarcodesVariables = {
    productId: string;
    variantId: string;
    body: VariantBarcodesUpdateRequest;
};

const withSavedBarcodes = (
    cached: ApiResponse<GetProductDetailResponse> & { success: true },
    saved: ProductVariant,
): ApiResponse<GetProductDetailResponse> => ({
    ...cached,
    response: {
        ...cached.response,
        variants: cached.response.variants.map((variant) =>
            variant.id === saved.id
                ? {
                      ...variant,
                      barcode: saved.barcode,
                      alternateBarcodes: saved.alternateBarcodes,
                      barcodeGenerated: saved.barcodeGenerated,
                      barcodeKept: saved.barcodeKept,
                  }
                : variant,
        ),
    },
});

/**
 * Replaces one variant's main barcode and alternates (bos-api API-195): add,
 * remove and make main each send the whole set. On success it first writes the
 * saved barcodes into every cached detail of the product (under every branch),
 * so the drawer reopened before the refetch lands seeds from the saved set and
 * a second save cannot send the old one; only that variant's barcode fields
 * change (the codes, and the generated and kept flags a new main clears), as
 * the reply counts stock for the caller's scope, not the cached branch's. Then it invalidates that detail, every list cache, as
 * the product update does, since the list's search and barcode filter match
 * alternates, and every Barcode issues page, which lists the old code until it
 * is read again (review of #241, finding 8).
 * Unwraps the envelope (rulebook §10): it resolves with the saved
 * `ProductDetail`, and a refusal rejects with the `ErrorResponse`, whose
 * `code` narrows with `isProductBarcodeErrorCode` to the field it belongs to.
 * The cached details stay envelopes, as `useProductDetailQuery` stores them.
 */
export const useUpdateVariantBarcodesMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<
        GetProductDetailResponse,
        ErrorResponse,
        UpdateVariantBarcodesVariables
    >({
        mutationFn: async ({ productId, variantId, body }) => {
            const result = await apiClient.products.updateVariantBarcodes(
                productId,
                variantId,
                body,
            );
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        onSuccess: (detail, variables) => {
            const saved = detail.variants.find(
                (candidate) => candidate.id === variables.variantId,
            );
            if (saved) {
                queryClient.setQueriesData<
                    ApiResponse<GetProductDetailResponse>
                >(
                    { queryKey: ProductQueryKeys.detail(variables.productId) },
                    (cached) =>
                        cached?.success
                            ? withSavedBarcodes(cached, saved)
                            : cached,
                );
            }
            queryClient.invalidateQueries({
                queryKey: ProductQueryKeys.detail(variables.productId),
            });
            queryClient.invalidateQueries({
                queryKey: ProductQueryKeys.lists(),
            });
            queryClient.invalidateQueries({
                queryKey: ProductQueryKeys.barcodeIssueLists(),
            });
        },
    });
};
